"use client";

import type { Voice } from "@/app/_data/voices";
import type { LanguageMetadata } from "@components/languages/config";
import type { TTSModels } from "@mw/shared/models";
import type { SelectTriggerComponentProps } from "@ui/components/core/Select";

import { Badge } from "@components/core/Badge";
import { Avatar } from "@ui/components/core/Avatar";
import { Button, ButtonProps } from "@ui/components/core/Button";
import { LanguageSelector } from "@ui/components/core/LanguageSelector";
import { Range } from "@ui/components/core/Range";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@ui/components/core/Select";
import { getVoiceImage } from "@ui/components/helpers/voiceUtils";
import Download20 from "@ui/icons/20x20/download-20.svg";
import { cn } from "@ui/lib/utils";
import { useTranslations } from "next-intl";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";

import { playingStateIcon } from "../config";

export const PlayButton = ({
  className,
  playingState,
  onPlay,
  variant,
  showLabel = false,
  disabled,
  willRegenerate = true,
}: {
  className?: string;
  disabled?: boolean;
  onPlay: () => void;
  playingState: string;
  showLabel?: boolean;
  variant?: "default" | "sidebar" | "simple";
  willRegenerate?: boolean;
}) => {
  return (
    <Button
      className={cn(
        "p-0",
        variant === "sidebar" ? "w-full" : showLabel ? "ml-16 w-fit px-12 pr-16" : "ml-16 w-40",
        variant === "simple" && "px-16",
        className,
      )}
      disabled={disabled}
      onClick={onPlay}
      size='md'
    >
      <span className='flex items-center gap-8' key={playingState}>
        {(variant !== "simple" || playingState === "loading") && playingStateIcon[playingState]}
        {(variant === "sidebar" || showLabel) &&
          (playingState === "playing" ? "Pause" : willRegenerate && variant === "simple" ? "Generate" : "Play")}
      </span>
    </Button>
  );
};

export const DownloadButton = ({
  audioElementRef,
  canDownload,
  completeAudioData,
  variant,
  className,
  buttonVariant,
  buttonSize,
  onAfterDownload,
}: {
  audioElementRef: React.RefObject<HTMLAudioElement | null>;
  buttonSize?: ButtonProps["size"];
  buttonVariant?: ButtonProps["variant"];
  canDownload: boolean;
  className?: string;
  completeAudioData: Uint8Array | null;
  onAfterDownload?: () => void;
  variant?: "default" | "sidebar";
}) => {
  return (
    <Button
      aria-label='Download generated speech'
      className={cn("p-0", variant === "sidebar" ? "w-full" : "ml-12 w-40", !canDownload && "opacity-50", className)}
      disabled={!canDownload}
      onClick={() => {
        const url = completeAudioData
          ? URL.createObjectURL(new Blob([completeAudioData as BlobPart], { type: "audio/mpeg" }))
          : audioElementRef.current?.src;

        if (!url) return;

        captureEvent(EventName.mw_tts_download_clicked, {
          has_audio_data: !!completeAudioData,
        });

        const link = document.createElement("a");
        link.href = url;
        link.download = "ElevenLabs_Text_to_Speech_audio.mp3";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        onAfterDownload?.();
      }}
      size={buttonSize}
      variant={buttonVariant || "elevated"}
    >
      <span className='flex items-center gap-8'>
        <Download20 />
        {variant === "sidebar" && "Download"}
      </span>
    </Button>
  );
};

export const VoiceSelector = ({
  voices,
  selectedVoice,
  onVoiceChange,
  size,
  variant = "default",
  showTags = false,
  loading = false,
  showSelectedAvatar = true,
  triggerVariant,
  overlayStyle,
  triggerClassName,
}: {
  loading?: boolean;
  onVoiceChange: (voice: Voice) => void;
  selectedVoice: Voice | undefined;
  showTags?: boolean;
  size: "md" | "xl";
  variant?: "default" | "sidebar" | "simple";
  showSelectedAvatar?: boolean;
  voices: Voice[];
  triggerVariant?: "outline-new";
  overlayStyle?: boolean;
  triggerClassName?: string;
}) => {
  const t = useTranslations("ClientKeys.TTS");
  return (
    <div className='flex flex-col gap-4'>
      {variant === "sidebar" && <div className='f-ui-04'>{t("widget.voice")}</div>}
      <Select
        disabled={loading}
        onValueChange={voiceId => {
          const voice = voices.find(v => v.voiceId === voiceId);
          if (voice) onVoiceChange(voice);
        }}
        value={selectedVoice?.voiceId}
      >
        <SelectTrigger
          className={cn(
            showSelectedAvatar ? "w-[180px]" : "w-[150px]",
            variant === "sidebar" && "w-full",
            loading && "opacity-50",
            triggerClassName,
          )}
          icon={
            showSelectedAvatar && <Avatar height={20} image={getVoiceImage(selectedVoice, size)} size='xs' width={20} />
          }
          size='md'
          variant={triggerVariant}
          overlayStyle={overlayStyle}
        >
          <SelectValue>{loading ? t("widget.loading_voices") : (selectedVoice?.name ?? "")}</SelectValue>
        </SelectTrigger>
        <SelectContent className='max-h-[350px]'>
          {voices.map(voice => {
            return (
              <SelectItem
                icon={<Avatar height={20} image={getVoiceImage(voice, size)} size='xs' width={20} />}
                key={voice.voiceId}
                value={voice.voiceId}
                fontVariant={variant === "simple" ? "new" : "default"}
              >
                {voice.name}
                {showTags && voice?.tags?.length !== 0 && (
                  <div className='ml-8 inline-flex flex-wrap gap-4'>
                    {voice?.tags?.map(tag => (
                      <Badge className='bg-overlay-dark' key={tag.name}>
                        {tag.name}
                      </Badge>
                    ))}
                  </div>
                )}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
};

export const SpeedSelector = ({ value, onSpeedChange }: { onSpeedChange: (value: number) => void; value: number }) => {
  const t = useTranslations("ClientKeys.TTS");
  return (
    <div className='flex flex-col gap-12'>
      <div className='f-ui-04'>{t("widget.speed")}</div>
      <Range max={1.2} min={0.7} onValueChange={value => onSpeedChange(value[0])} step={0.01} value={[value]} />
    </div>
  );
};

export const ModelSelector = ({
  selectedModel,
  onModelChange,
  models,
}: {
  models: TTSModels[];
  onModelChange: (modelId: TTSModels) => void;
  selectedModel: TTSModels;
}) => {
  const t = useTranslations("ClientKeys.TTS");
  return models.length > 1 ? (
    <div className='flex flex-col gap-4'>
      <div className='f-ui-04'>{t("widget.model")}</div>
      <Select onValueChange={value => onModelChange(value as TTSModels)} value={selectedModel}>
        <SelectTrigger className='w-full'>
          <SelectValue>{t(`widget.${selectedModel}`)}</SelectValue>
        </SelectTrigger>
        <SelectContent className='max-h-[350px]'>
          {models.map(model => (
            <SelectItem key={model} value={model}>
              {t(`widget.${model}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  ) : null;
};

export const LanguageSelect = ({
  language,
  onLanguageChange,
  sortedLanguages,
  variant = "default",
  showSelectedLabel = false,
  showDropdownArrow = true,
  showFlag = true,
  triggerSize = "md",
  triggerVariant,
  overlayStyle,
}: {
  language: LanguageMetadata | undefined;
  onLanguageChange: (language: string) => void;
  showDropdownArrow?: boolean;
  showSelectedLabel?: boolean;
  sortedLanguages: LanguageMetadata[];
  triggerSize?: SelectTriggerComponentProps["size"];
  variant?: "default" | "sidebar" | "simple";
  showFlag?: boolean;
  triggerVariant?: "outline-new" | "elevated";
  overlayStyle?: boolean;
}) => {
  return (
    <LanguageSelector
      fullWidth={variant === "sidebar"}
      language={language}
      setSelectedLanguage={onLanguageChange}
      showDropdownArrow={showDropdownArrow}
      showDropdownLabel={variant === "sidebar"}
      showSelectedLabel={showSelectedLabel || variant === "sidebar"}
      sortedLanguages={sortedLanguages}
      triggerSize={triggerSize}
      showFlag={showFlag}
      triggerVariant={triggerVariant}
      overlayStyle={overlayStyle}
    />
  );
};
